import React, { PureComponent } from 'react';
import { Theme, WithStyles, withStyles } from '@lifeomic/chroma-react/styles';
import SurveyItem from '../../util/SurveyItem';
import ChoiceOptions from '../../util/ChoiceOptions';
import { withSurveyContext, WithSurveyContextInjectedProps } from '../Contexts';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableGroupItem from './TableGroupItem';
import { Text } from '@lifeomic/chroma-react/components/Text';
import withCheckForEnablement, {
  WithCheckForEnablementInjectedProps,
} from '../hoc/withCheckForEnablement';
import getSurveyItemTestId from '../../util/getSurveyItemTestId';
import { getTestProps } from '../../../../util/testProps';
import withIsMobile, {
  WithIsMobileInjectedProps,
} from '../../../../hoc/withIsMobile';
import TableGroupItemMobileVariant from './TableGroupItemMobileVariant';

export const testIds = {
  tableGroup: (linkId: string) => getSurveyItemTestId(linkId),
};

const styles = (theme: Theme) => ({
  root: {
    marginTop: theme.spacing(5),
  },
  question: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  cellHead: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(2, 1),
  },
});

export interface TableGroupOwnProps {
  surveyItem: SurveyItem;
}

export type TableGroupProps = WithStyles<typeof styles> &
  WithIsMobileInjectedProps &
  WithCheckForEnablementInjectedProps &
  TableGroupOwnProps &
  WithSurveyContextInjectedProps;

class TableGroup extends PureComponent<TableGroupProps> {
  choiceOptions: ChoiceOptions;

  constructor(props: TableGroupProps) {
    super(props);

    const firstChoiceChild = props.surveyItem.subItems[0];
    if (!firstChoiceChild) {
      throw new Error(
        'Items with the table group modifier extension must have choice sub items',
      );
    }

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this.choiceOptions = props.survey.getChoiceOptionsForItem(
      firstChoiceChild.link,
    );

    if (!this.choiceOptions) {
      throw new Error(
        'Items with the table group modifier extension must have choice sub items with options',
      );
    }
  }

  getHeaderText = () => {
    if (!this.props.surveyItem.hasText) return null;

    const { classes } = this.props;

    return (
      <Text size="body" className={classes.question}>
        {this.props.surveyItem.text}
      </Text>
    );
  };

  render() {
    const { surveyItem, classes, isEnabled, isMobile } = this.props;

    if (!isEnabled) return null;

    return (
      <div
        className={classes.root}
        {...getTestProps(testIds.tableGroup(surveyItem.link))}
      >
        {this.getHeaderText()}
        {isMobile && !surveyItem.shouldForceMatrixTableVariant ? (
          surveyItem.subItems.map(subItem => (
            <TableGroupItemMobileVariant
              key={subItem.link}
              surveyItem={subItem}
              choiceOptions={this.choiceOptions}
            />
          ))
        ) : (
          <Table role="table">
            <TableHead>
              <TableRow>
                <TableCell></TableCell>
                {this.choiceOptions.options.map((option, index) => (
                  <TableCell
                    key={index}
                    classes={{ root: classes.cellHead }}
                    align="center"
                  >
                    {option.display}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {surveyItem.subItems.map(subItem => (
                <TableGroupItem
                  key={subItem.link}
                  surveyItem={subItem}
                  choiceOptions={this.choiceOptions}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    );
  }
}

export default withIsMobile(
  withStyles(styles)(withSurveyContext(withCheckForEnablement(TableGroup))),
);
